/**
 * HomeView Component - Refactored Home Screen
 * 
 * Structure (per specification):
 * 1. Sticky Quick Action Bar (Log Meal, Take Photo, Manual Entry) - ALWAYS VISIBLE
 * 2. Today's Progress Card (Calories + Macros)
 * 3. Conditional Goal Prompt (if SmartGoals not initialized) - disappears after setup
 * 4. MIQ Status Strip (compact single-line)
 * 5. Today's Meals Section with + Add Another Meal
 * 6. Activity Section (Steps/Exercise)
 * 7. Micronutrients Preview (collapsed by default)
 * 8. Intelligence Upsell Section (Smart/Genius Plaite) - NEVER above Today's Meals
 * 9. Footer (legal disclaimer + AI-powered text)
 */

import React, { useState } from 'react';
import { Camera, Utensils, Pencil, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Plus, Star, Trash2, Activity, Target, Zap, Sparkles, Edit3, Calendar } from 'lucide-react';
import { brand, getPrimaryButtonStyle, getSecondaryButtonStyle, getCardStyle } from '../../styles/brand';
import MIQCalibrationBanner from '../miq/MIQCalibrationBanner';
import { CONFIDENCE_FACTOR_CONFIG } from '../miq/MIQView';
import HealthIntelligenceCard from './HealthIntelligenceCard';
import BloodworkHomeCard from './BloodworkHomeCard';
import TrialPromoCard from './TrialPromoCard';
import TrialCountdownBanner from '../onboarding/TrialCountdownBanner';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK ACTION BAR - Always visible, sticky below header
// "No scrolling required to log a meal"
// ═══════════════════════════════════════════════════════════════════════════════
const QuickActionBar = ({ onTakePhoto, onLogMeal, onManualEntry, onBarcode }) => {
  const actionStyle = (bg, glow) => ({
    flex: 1,
    padding: '12px 6px',
    background: bg,
    border: 'none',
    borderRadius: radius.lg,
    color: colors.text,
    fontFamily: typography.fontFamily,
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    boxShadow: glow,
    transition: 'transform 0.15s ease, opacity 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div data-testid="quick-action-bar" className="quick-action-bar">
      <div style={{ display: 'flex', gap: spacing.s3, justifyContent: 'center' }}>
        <button data-testid="quick-action-photo" onClick={onTakePhoto}
          style={actionStyle(gradients.primary, shadows.glow)}>
          <Camera size={18} />
          <span>Photo</span>
        </button>
        <button data-testid="quick-action-barcode" onClick={onBarcode}
          style={actionStyle(gradients.primary, shadows.glow)}>
          <span style={{ fontSize: '16px', fontFamily: 'monospace', fontWeight: '900', letterSpacing: '-2px', lineHeight: '18px' }}>||||</span>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
            <span>Barcode</span>
            <span style={{ fontSize: '8px', opacity: 0.85, fontWeight: '500', letterSpacing: '0.02em' }}>or Label</span>
          </span>
        </button>
        <button data-testid="quick-action-log-meal" onClick={onLogMeal}
          style={actionStyle(gradients.freeGrad, shadows.glowFree)}>
          <Utensils size={18} />
          <span>Log Meal</span>
        </button>
        <button data-testid="quick-action-manual" onClick={onManualEntry}
          style={actionStyle(colors.glass, `inset 0 0 0 1px ${colors.stroke}`)}>
          <Pencil size={18} />
          <span>Manual</span>
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TODAY'S PROGRESS CARD - Compact calories + macros with reduced padding
// Supports Flexible Macro Mode: only calories + protein enforced for success
// ═══════════════════════════════════════════════════════════════════════════════
const TodaysProgressCard = ({ 
  calories, 
  dailyGoal, 
  protein, 
  carbs, 
  fat,
  fiber = 0,
  targetProtein, 
  targetCarbs, 
  targetFat,
  hasAiGoals,
  totalCaloriesBurned = 0,
  flexibleMacroMode = false,
  calorieWindow = null
}) => {
  const remaining = Math.max(0, dailyGoal - calories + totalCaloriesBurned);
  const progressPercent = Math.min(100, Math.round((calories / dailyGoal) * 100));
  const proteinPercent = Math.min(100, Math.round((protein / targetProtein) * 100));
  const carbsPercent = Math.min(100, Math.round((carbs / targetCarbs) * 100));
  const fatPercent = Math.min(100, Math.round((fat / targetFat) * 100));
  const netCarbs = Math.max(0, Math.round((carbs - fiber) * 10) / 10);
  
  // SVG ring dimensions
  const ringSize = 160;
  const strokeWidth = 6;
  const ringRadius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * ringRadius;
  const strokeDash = (progressPercent / 100) * circumference;

  const MacroBar = ({ value, target, percent, label, color }) => (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontFamily: typography.monoFont, fontSize: '18px', fontWeight: '600', color, marginBottom: '2px', letterSpacing: '-0.02em' }}>
        {value}<span style={{ fontSize: '11px', fontWeight: '400', color: colors.mutedText }}>/{target}g</span>
      </div>
      <div style={{ fontSize: '10px', color: colors.textSecondary, marginBottom: '6px', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: '500' }}>
        {label}
      </div>
      <div style={{ width: '100%', height: '2px', background: 'rgba(255,255,255,0.05)', borderRadius: '1px' }}>
        <div style={{ 
          width: `${percent}%`, height: '100%', borderRadius: '1px',
          background: color, transition: 'width 0.5s ease',
          boxShadow: percent > 0 ? `0 0 8px ${color}50` : 'none'
        }} />
      </div>
    </div>
  );
  
  return (
    <div data-testid="todays-progress-card" style={{
      ...getCardStyle({ elevated: true }),
      padding: spacing.s6,
      marginBottom: spacing.s4,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle radial glow behind ring */}
      <div style={{
        position: 'absolute', top: '-30px', left: '50%', transform: 'translateX(-50%)',
        width: '240px', height: '240px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,92,252,0.04) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />
      
      {/* Calorie Ring + Center */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: spacing.s5, position: 'relative' }}>
        <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
          {/* Track */}
          <circle cx={ringSize/2} cy={ringSize/2} r={ringRadius}
            fill="none" stroke={colors.glass} strokeWidth={strokeWidth} />
          {/* Progress arc */}
          <circle cx={ringSize/2} cy={ringSize/2} r={ringRadius}
            fill="none" stroke="url(#calorieGrad)" strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${strokeDash} ${circumference - strokeDash}`}
            transform={`rotate(-90 ${ringSize/2} ${ringSize/2})`}
            style={{ transition: 'stroke-dasharray 0.5s ease', filter: progressPercent > 0 ? 'drop-shadow(0 0 4px rgba(124,92,252,0.4))' : 'none' }}
          />
          <defs>
            <linearGradient id="calorieGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.accent} />
              <stop offset="50%" stopColor={colors.smart} />
              <stop offset="100%" stopColor={colors.free} />
            </linearGradient>
          </defs>
        </svg>
        {/* Center text */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}>
          <span style={{ fontFamily: typography.monoFont, fontSize: '32px', fontWeight: '600', color: colors.text, lineHeight: 1, letterSpacing: '-0.03em' }}>
            {calories}
          </span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: '11px', color: colors.mutedText, marginTop: '4px', letterSpacing: '0.05em' }}>
            of {dailyGoal} kcal
          </span>
        </div>
      </div>
      
      {/* Remaining badge */}
      <div style={{ textAlign: 'center', marginBottom: spacing.s5 }}>
        <span style={{ 
          fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em',
          color: remaining > 0 ? colors.free : colors.warning,
          background: remaining > 0 ? colors.freeDim : 'rgba(245,158,11,0.10)',
          padding: '5px 16px', borderRadius: radius.pill
        }}>
          {remaining > 0 ? `${remaining} left` : 'Goal reached!'}
        </span>
        {hasAiGoals && (
          <span style={{ 
            marginLeft: '6px', fontSize: '9px',
            background: gradients.primary, color: colors.text,
            padding: '3px 8px', borderRadius: radius.pill, fontWeight: '600'
          }}>AI Goals</span>
        )}
      </div>
      
      {/* Exercise burned */}
      {totalCaloriesBurned > 0 && (
        <div style={{ 
          textAlign: 'center', marginBottom: spacing.s3,
          fontSize: '11px', color: colors.free,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
        }}>
          <Activity size={12} />
          Exercise: -{totalCaloriesBurned} kcal (Net: {calories - totalCaloriesBurned})
        </div>
      )}
      
      {/* Flexible mode */}
      {flexibleMacroMode && (
        <div style={{
          marginBottom: spacing.s3, padding: '6px 12px',
          background: colors.freeDim, border: `1px solid ${colors.freeBorder}`,
          borderRadius: radius.sm, textAlign: 'center',
          fontSize: '10px', color: colors.free, fontWeight: '500'
        }}>
          Flexible Mode: Focus on calories + protein
        </div>
      )}
      
      {/* Macro bars */}
      <div style={{ display: 'flex', gap: spacing.s3 }}>
        <MacroBar value={protein} target={targetProtein} percent={proteinPercent} label="Protein" color={colors.protein} />
        <MacroBar value={carbs} target={targetCarbs} percent={carbsPercent} label="Carbs" color={colors.carbs} />
        <MacroBar value={fat} target={targetFat} percent={fatPercent} label="Fat" color={colors.fat} />
      </div>
      
      {/* Net carbs keto */}
      {fiber > 0 && (
        <div style={{ textAlign: 'center', marginTop: spacing.s2 }}>
          <span style={{ 
            fontSize: '10px', color: colors.fiber,
            background: colors.geniusDim, padding: '2px 8px',
            borderRadius: radius.pill, fontWeight: '600'
          }}>
            Net: {netCarbs}g KETO
          </span>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// GOAL PROMPT - Only shows if SmartGoals not initialized (disappears permanently)
// Coach-style prompt with "Activate SmartGoals" CTA
// ═══════════════════════════════════════════════════════════════════════════════
const GoalPrompt = ({ onSetGoals }) => (
  <div 
    data-testid="goal-prompt"
    style={{
      padding: spacing.s4,
      background: `linear-gradient(135deg, ${colors.accentGlow} 0%, rgba(118, 75, 162, 0.12) 100%)`,
      border: `1px solid rgba(102, 126, 234, 0.3)`,
      borderRadius: radius.lg,
      marginBottom: spacing.s3
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.s3, marginBottom: spacing.s3 }}>
      <Target size={20} style={{ color: colors.accent, flexShrink: 0, marginTop: '2px' }} />
      <div>
        <span style={{ fontWeight: '700', fontSize: '15px', color: colors.text, display: 'block', marginBottom: '4px' }}>
          Fuel your goals with intelligence.
        </span>
        <p style={{ 
          margin: 0, 
          fontSize: '12px', 
          color: colors.textSecondary,
          lineHeight: '1.5'
        }}>
          Set SmartGoals to activate personalized metabolic guidance powered by MIQ.
        </p>
      </div>
    </div>
    <button
      data-testid="set-smartgoals-btn"
      onClick={onSetGoals}
      style={{
        width: '100%',
        padding: `${spacing.s3} ${spacing.s4}`,
        background: gradients.primary,
        border: 'none',
        borderRadius: radius.md,
        color: colors.text,
        fontFamily: typography.fontFamily,
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        boxShadow: shadows.glow
      }}
    >
      🎯 Activate SmartGoals
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MIQ STATUS STRIP - Compact single-line clickable strip
// ═══════════════════════════════════════════════════════════════════════════════
const MIQStatusStrip = ({ status, factorsActive, totalFactors, onNavigate }) => {
  // Determine MIQ state based on factors completed
  const miqState = factorsActive === 0 ? 'not_started' 
    : factorsActive < totalFactors ? 'in_progress' 
    : 'completed';
  
  // Not Started State - Show onboarding prompt
  if (miqState === 'not_started') {
    return (
      <div
        data-testid="miq-onboarding-card"
        style={{
          padding: spacing.s4,
          background: `linear-gradient(135deg, ${colors.accentGlow} 0%, rgba(118, 75, 162, 0.12) 100%)`,
          border: `1px solid rgba(102, 126, 234, 0.25)`,
          borderRadius: radius.lg,
          marginBottom: spacing.s3
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.s3, marginBottom: spacing.s3 }}>
          <span style={{ fontSize: '24px' }}>🧠</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              fontSize: typography.body.fontSize, 
              fontWeight: '700', 
              color: colors.text,
              margin: `0 0 ${spacing.s1} 0`
            }}>
              Let&apos;s build your MIQ Baseline
            </h3>
            <p style={{ 
              fontSize: typography.bodySmall.fontSize, 
              color: colors.textSecondary,
              margin: 0,
              lineHeight: '1.4'
            }}>
              Answer a few quick questions so SmartPlAIte can personalize your nutrition.
            </p>
          </div>
        </div>
        <button
          data-testid="start-miq-setup-btn"
          onClick={onNavigate}
          style={{
            width: '100%',
            padding: `${spacing.s3} ${spacing.s4}`,
            background: gradients.primary,
            border: 'none',
            borderRadius: radius.md,
            color: colors.text,
            fontSize: typography.body.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.s2
          }}
        >
          <Sparkles size={18} />
          Start MIQ Setup
        </button>
      </div>
    );
  }
  
  // In Progress State - Show learning card
  if (miqState === 'in_progress') {
    return (
      <button
        data-testid="miq-status-strip"
        onClick={onNavigate}
        style={{
          width: '100%',
          padding: `${spacing.s3} ${spacing.s4}`,
          background: `linear-gradient(135deg, ${colors.accentGlow} 0%, rgba(118, 75, 162, 0.08) 100%)`,
          border: `1px solid rgba(102, 126, 234, 0.2)`,
          borderRadius: radius.md,
          marginBottom: spacing.s3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s3 }}>
          <span style={{ fontSize: '20px' }}>🧠</span>
          <div>
            <div style={{ 
              fontSize: typography.body.fontSize, 
              fontWeight: '600', 
              color: colors.text,
              marginBottom: '2px'
            }}>
              MIQ: Learning ({factorsActive}/{totalFactors} factors)
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: colors.textSecondary 
            }}>
              Continue setup to unlock personalized insights
            </div>
          </div>
        </div>
        <ChevronRight size={18} color={colors.accent} />
      </button>
    );
  }
  
  // Completed State - Show baseline ready card
  return (
    <button
      data-testid="miq-baseline-ready-card"
      onClick={onNavigate}
      style={{
        width: '100%',
        padding: `${spacing.s3} ${spacing.s4}`,
        background: `linear-gradient(135deg, rgba(56, 239, 125, 0.1) 0%, rgba(102, 126, 234, 0.08) 100%)`,
        border: `1px solid rgba(56, 239, 125, 0.25)`,
        borderRadius: radius.md,
        marginBottom: spacing.s3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        textAlign: 'left'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s3 }}>
        <span style={{ fontSize: '20px' }}>✨</span>
        <div>
          <div style={{ 
            fontSize: typography.body.fontSize, 
            fontWeight: '600', 
            color: colors.text,
            marginBottom: '2px'
          }}>
            Your MIQ Baseline is Ready
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: colors.textSecondary 
          }}>
            View your personalized intelligence report
          </div>
        </div>
      </div>
      <ChevronRight size={18} color={colors.success} />
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MEAL CARD - Individual meal item with favorite + remove buttons
// ═══════════════════════════════════════════════════════════════════════════════
const MealCard = ({ meal, onFavorite, onRemove, onEditPortion, isFavorite }) => (
  <div 
    data-testid={`meal-card-${meal.meal_id}`}
    style={{
      padding: `${spacing.s3} ${spacing.s3}`,
      background: colors.panel,
      border: `1px solid ${colors.stroke}`,
      borderRadius: radius.md,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: spacing.s3
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ 
        fontWeight: '600', 
        fontSize: typography.bodySmall.fontSize, 
        color: colors.text,
        marginBottom: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        {meal.food_items?.length > 35 ? meal.food_items.substring(0, 35) + '...' : meal.food_items || 'Meal'}
        {meal.portion_percent && meal.portion_percent < 100 && (
          <span style={{ 
            fontSize: '10px', 
            background: 'rgba(102, 126, 234, 0.2)', 
            color: '#667eea',
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: '500'
          }}>
            {meal.portion_percent}%
          </span>
        )}
      </div>
      <div style={{ 
        fontSize: '11px', 
        color: colors.textSecondary
      }}>
        {meal.calories} kcal • P: {meal.protein || 0}g • C: {meal.carbs || 0}g • F: {meal.fat || 0}g
      </div>
    </div>
    <div style={{ display: 'flex', gap: spacing.s2, flexShrink: 0 }}>
      {/* Edit Portion Button */}
      <button
        data-testid={`edit-portion-${meal.meal_id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onEditPortion) onEditPortion(meal);
        }}
        style={{
          padding: '10px',
          minWidth: '40px',
          minHeight: '40px',
          background: 'rgba(102, 126, 234, 0.08)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'rgba(102, 126, 234, 0.3)',
          touchAction: 'manipulation'
        }}
        title="Edit portion eaten"
      >
        <Edit3 size={16} color="#667eea" />
      </button>
      <button
        data-testid={`favorite-meal-${meal.meal_id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[MealCard] Favorite clicked for meal:', meal.meal_id);
          if (onFavorite) onFavorite(meal);
        }}
        style={{
          padding: '10px',
          minWidth: '40px',
          minHeight: '40px',
          background: isFavorite ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255,255,255,0.08)',
          border: isFavorite ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'rgba(255, 215, 0, 0.3)',
          touchAction: 'manipulation'
        }}
        title={isFavorite ? 'Saved' : 'Save to Favorites'}
      >
        <Star 
          size={18} 
          fill={isFavorite ? '#FFD700' : 'none'} 
          color={isFavorite ? '#FFD700' : colors.textSecondary} 
        />
      </button>
      <button
        data-testid={`remove-meal-${meal.meal_id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[MealCard] Delete clicked for meal:', meal.meal_id);
          if (onRemove) onRemove(meal.meal_id);
        }}
        style={{
          padding: '10px',
          minWidth: '40px',
          minHeight: '40px',
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'rgba(239, 68, 68, 0.3)',
          touchAction: 'manipulation'
        }}
        title="Delete Meal"
      >
        <Trash2 size={18} color="#ef4444" />
      </button>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// DATE NAVIGATOR - Allows viewing and editing past days' logs
// ═══════════════════════════════════════════════════════════════════════════════
const DateNavigator = ({ selectedDate, onDateChange }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const selected = selectedDate ? new Date(selectedDate + 'T00:00:00') : today;
  const isToday = !selectedDate || selected.toISOString().slice(0, 10) === today.toISOString().slice(0, 10);
  
  const formatDate = (d) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = Math.round((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  const goBack = () => {
    const prev = new Date(selected);
    prev.setDate(prev.getDate() - 1);
    // Cap at 90 days back
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - 90);
    if (prev < minDate) return;
    onDateChange(prev.toISOString().slice(0, 10));
  };
  
  const goForward = () => {
    if (isToday) return;
    const next = new Date(selected);
    next.setDate(next.getDate() + 1);
    const nextStr = next.toISOString().slice(0, 10);
    const todayStr = today.toISOString().slice(0, 10);
    onDateChange(nextStr === todayStr ? null : nextStr);
  };
  
  const goToToday = () => onDateChange(null);
  
  return (
    <div data-testid="date-navigator" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '12px', padding: '8px 0', marginBottom: '8px'
    }}>
      <button data-testid="date-nav-prev" onClick={goBack} style={{
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: colors.text,
        display: 'flex', alignItems: 'center'
      }}>
        <ChevronLeft size={16} />
      </button>
      
      <button data-testid="date-nav-label" onClick={goToToday} style={{
        background: isToday ? 'transparent' : 'rgba(102, 126, 234, 0.1)',
        border: isToday ? 'none' : '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '8px', padding: '6px 16px', cursor: isToday ? 'default' : 'pointer',
        color: isToday ? colors.text : '#667eea', fontWeight: '600', fontSize: '13px',
        display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px',
        justifyContent: 'center'
      }}>
        <Calendar size={14} />
        {formatDate(selected)}
      </button>
      
      <button data-testid="date-nav-next" onClick={goForward} disabled={isToday} style={{
        background: isToday ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
        padding: '6px 10px', cursor: isToday ? 'not-allowed' : 'pointer',
        color: isToday ? colors.textSecondary : colors.text, opacity: isToday ? 0.4 : 1,
        display: 'flex', alignItems: 'center'
      }}>
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TODAY'S MEALS SECTION - With + Add Another Meal button at bottom
// ═══════════════════════════════════════════════════════════════════════════════
const TodaysMealsSection = ({ meals, favorites, onFavorite, onRemove, onEditPortion, onAddMeal, selectedDate, onDateChange }) => {
  const favoriteIds = favorites?.map(f => f.meal_id || f.original_meal_id) || [];
  const isViewingPastDate = !!selectedDate;
  
  return (
    <div 
      data-testid="todays-meals-section"
      style={{
        ...getCardStyle({ elevated: true }),
        padding: spacing.s3,
        marginBottom: spacing.s2,
        borderRadius: radius.md
      }}
    >
      {/* Date Navigator */}
      <DateNavigator selectedDate={selectedDate} onDateChange={onDateChange} />
      
      <h3 style={{ 
        margin: `0 0 ${spacing.s3} 0`, 
        fontSize: '14px', 
        fontWeight: '600',
        color: colors.text
      }}>
        {isViewingPastDate ? 'Meals' : "Today's Meals"}
      </h3>
      
      {meals.length === 0 ? (
        <p style={{ 
          textAlign: 'center', 
          color: colors.textSecondary, 
          fontSize: typography.bodySmall.fontSize,
          padding: `${spacing.s4} 0`
        }}>
          {isViewingPastDate ? 'No meals logged for this day.' : 'No meals logged yet. Start by adding your first meal!'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.s2 }}>
          {meals.map(meal => (
            <MealCard
              key={meal.meal_id}
              meal={meal}
              onFavorite={onFavorite}
              onRemove={onRemove}
              onEditPortion={onEditPortion}
              isFavorite={favoriteIds.includes(meal.meal_id)}
            />
          ))}
        </div>
      )}
      
      {/* Add Meal Button */}
      <button
        data-testid={meals.length === 0 ? "log-first-meal-btn" : "add-another-meal-btn"}
        onClick={onAddMeal}
        style={{
          width: '100%',
          marginTop: spacing.s3,
          padding: spacing.s3,
          background: meals.length === 0 
            ? gradients.primary
            : 'rgba(76, 175, 80, 0.1)',
          border: meals.length === 0 
            ? 'none' 
            : `1px dashed rgba(76, 175, 80, 0.4)`,
          borderRadius: radius.md,
          color: meals.length === 0 ? colors.text : colors.success,
          fontFamily: typography.fontFamily,
          fontSize: typography.bodySmall.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px'
        }}
      >
        <Plus size={14} />
        {isViewingPastDate ? 'Add Meal to This Day' : (meals.length === 0 ? 'Log Your First Meal' : 'Add Another Meal')}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY SECTION - Steps & Exercise with CTA: Log Activity
// ═══════════════════════════════════════════════════════════════════════════════
const ActivitySection = ({ exercises, totalCaloriesBurned, dailySteps, onLogActivity, onLogSteps, onDeleteActivity }) => {
  const [stepInput, setStepInput] = useState('');
  const [isLoggingSteps, setIsLoggingSteps] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const handleLogSteps = async () => {
    const steps = parseInt(stepInput);
    if (!steps || steps <= 0) return;
    
    setIsLoggingSteps(true);
    try {
      await onLogSteps(steps);
      setStepInput('');
    } catch (e) {
      console.error('Error logging steps:', e);
    } finally {
      setIsLoggingSteps(false);
    }
  };
  
  const handleDeleteActivity = async (exerciseId) => {
    if (!onDeleteActivity || !exerciseId) return;
    
    if (!window.confirm('Delete this activity?')) return;
    
    setDeletingId(exerciseId);
    try {
      await onDeleteActivity(exerciseId);
    } catch (e) {
      console.error('Error deleting activity:', e);
    } finally {
      setDeletingId(null);
    }
  };
  
  return (
    <div 
      data-testid="activity-section"
      style={{
        ...getCardStyle({ elevated: true }),
        padding: spacing.s3,
        marginBottom: spacing.s2,
        borderRadius: radius.md
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: spacing.s3
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
          <Activity size={16} style={{ color: colors.success }} />
          <span style={{ fontSize: typography.bodySmall.fontSize, fontWeight: '600', color: colors.text }}>
            Activity
          </span>
          {totalCaloriesBurned > 0 && (
            <span style={{ 
              fontSize: '11px', 
              color: colors.success,
              fontWeight: '600'
            }}>
              -{totalCaloriesBurned} kcal
            </span>
          )}
        </div>
        <button
          data-testid="log-activity-btn"
          onClick={onLogActivity}
          style={{
            padding: `${spacing.s2} ${spacing.s3}`,
            background: 'rgba(76, 175, 80, 0.1)',
            border: `1px solid rgba(76, 175, 80, 0.3)`,
            borderRadius: radius.sm,
            color: colors.success,
            fontFamily: typography.fontFamily,
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Log Activity
        </button>
      </div>
      
      {/* Step Tracker Input */}
      <div style={{
        display: 'flex',
        gap: spacing.s2,
        marginBottom: (dailySteps > 0 || exercises?.length > 0) ? spacing.s3 : 0,
        alignItems: 'center'
      }}>
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          gap: spacing.s2,
          padding: `${spacing.s2} ${spacing.s3}`,
          background: colors.panel,
          border: `1px solid ${colors.stroke}`,
          borderRadius: radius.md
        }}>
          <span style={{ fontSize: '16px' }}>🚶</span>
          <input
            data-testid="step-input"
            type="number"
            placeholder="Enter steps"
            value={stepInput}
            onChange={(e) => setStepInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogSteps()}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: colors.text,
              fontFamily: typography.fontFamily,
              fontSize: '14px',
              width: '100%'
            }}
          />
          <span style={{ fontSize: '11px', color: colors.textSecondary }}>steps</span>
        </div>
        <button
          data-testid="log-steps-btn"
          onClick={handleLogSteps}
          disabled={!stepInput || isLoggingSteps}
          style={{
            padding: `${spacing.s2} ${spacing.s4}`,
            background: stepInput ? `linear-gradient(135deg, ${colors.success} 0%, #8BC34A 100%)` : colors.panel2,
            border: 'none',
            borderRadius: radius.md,
            color: stepInput ? colors.text : colors.textSecondary,
            fontFamily: typography.fontFamily,
            fontSize: typography.bodySmall.fontSize,
            fontWeight: '600',
            cursor: stepInput ? 'pointer' : 'not-allowed',
            opacity: isLoggingSteps ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
        >
          {isLoggingSteps ? '...' : 'Log'}
        </button>
      </div>
      
      {/* Daily Steps Summary */}
      {dailySteps > 0 && (
        <div style={{
          padding: `${spacing.s2} ${spacing.s3}`,
          background: 'rgba(76, 175, 80, 0.08)',
          borderRadius: radius.sm,
          marginBottom: exercises?.length > 0 ? spacing.s2 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: typography.bodySmall.fontSize, color: colors.text }}>
            🚶 {dailySteps.toLocaleString()} steps today
          </span>
          <span style={{ fontSize: '11px', color: colors.success, fontWeight: '600' }}>
            ~{Math.round(dailySteps * 0.04)} kcal
          </span>
        </div>
      )}
      
      {/* Exercise List */}
      {exercises?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.s2 }}>
          {exercises.map((ex, idx) => (
            <div 
              key={ex.exercise_id || idx}
              data-testid={`activity-item-${ex.exercise_id || idx}`}
              style={{
                padding: `${spacing.s2} ${spacing.s3}`,
                background: 'rgba(76, 175, 80, 0.05)',
                borderRadius: radius.sm,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: spacing.s2
              }}
            >
              <span style={{ fontSize: typography.bodySmall.fontSize, color: colors.text, flex: 1 }}>
                {ex.activity} {ex.duration_minutes > 0 && `(${ex.duration_minutes} min)`}
              </span>
              <span style={{ fontSize: typography.bodySmall.fontSize, fontWeight: '600', color: colors.success }}>
                -{ex.calories_burned} kcal
              </span>
              {onDeleteActivity && ex.exercise_id && (
                <button
                  data-testid={`delete-activity-${ex.exercise_id}`}
                  onClick={() => handleDeleteActivity(ex.exercise_id)}
                  disabled={deletingId === ex.exercise_id}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px 8px',
                    cursor: deletingId === ex.exercise_id ? 'not-allowed' : 'pointer',
                    opacity: deletingId === ex.exercise_id ? 0.5 : 0.6,
                    fontSize: '14px',
                    borderRadius: radius.sm,
                    transition: 'opacity 0.2s, background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                    e.currentTarget.style.background = 'none';
                  }}
                  title="Delete activity"
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MICRONUTRIENTS PREVIEW - Below macros, labeled as Smart Plaite feature
// ═══════════════════════════════════════════════════════════════════════════════
const MicronutrientsPreview = ({ 
  micros, 
  rdaValues, 
  isLocked, 
  onUpgrade 
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Priority micronutrients with RDA values
  const microItems = [
    { label: 'Sugar', value: micros?.sugar || 0, rda: rdaValues?.sugar || 25, unit: 'g', isLimit: true, icon: '🍬' },
    { label: 'Sodium', value: micros?.sodium || 0, rda: rdaValues?.sodium || 2300, unit: 'mg', isLimit: true, icon: '🧂' },
    { label: 'Fiber', value: micros?.fiber || 0, rda: rdaValues?.fiber || 25, unit: 'g', isLimit: false, icon: '🌾' },
    { label: 'Cholesterol', value: micros?.cholesterol || 0, rda: rdaValues?.cholesterol || 300, unit: 'mg', isLimit: true, icon: '💔' },
    { label: 'Potassium', value: micros?.potassium || 0, rda: rdaValues?.potassium || 4700, unit: 'mg', isLimit: false, icon: '🍌' },
    { label: 'Calcium', value: micros?.calcium || 0, rda: rdaValues?.calcium || 1000, unit: 'mg', isLimit: false, icon: '🦴' },
    { label: 'Iron', value: micros?.iron || 0, rda: rdaValues?.iron || 18, unit: 'mg', isLimit: false, icon: '💪' },
    { label: 'Vitamin C', value: micros?.vitamin_c || 0, rda: rdaValues?.vitamin_c || 90, unit: 'mg', isLimit: false, icon: '🍊' },
    { label: 'Vitamin D', value: micros?.vitamin_d || 0, rda: rdaValues?.vitamin_d || 20, unit: 'mcg', isLimit: false, icon: '☀️' }
  ];
  
  // Filter to show items that have values > 0 or are priority micronutrients
  // Priority items are always shown to help users track key nutrients even at 0%
  const priorityLabels = ['Sugar', 'Sodium', 'Fiber', 'Cholesterol', 'Vitamin C', 'Vitamin D', 'Iron', 'Calcium'];
  const displayItems = microItems.filter(item => 
    item.value > 0 || priorityLabels.includes(item.label)
  );
  
  const avgPercent = displayItems.length > 0 
    ? Math.round(displayItems.reduce((sum, item) => sum + Math.min(100, (item.value / item.rda) * 100), 0) / displayItems.length)
    : 0;
  
  if (isLocked) {
    return (
      <button
        data-testid="micros-locked"
        onClick={onUpgrade}
        style={{
          width: '100%',
          padding: `${spacing.s3} ${spacing.s4}`,
          marginBottom: spacing.s2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          border: `1px solid rgba(56, 239, 125, 0.25)`,
          background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(17, 153, 142, 0.05) 100%)',
          borderRadius: radius.md
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <div>
            <span style={{ fontSize: typography.bodySmall.fontSize, color: colors.text, fontWeight: '500', display: 'block' }}>
              Micronutrients
            </span>
            <span style={{ fontSize: '10px', color: colors.textSecondary }}>
              Smart Plaite Feature
            </span>
          </div>
        </div>
        <span style={{ 
          fontSize: '10px', 
          background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)', 
          color: colors.text,
          padding: '3px 8px',
          borderRadius: radius.sm,
          fontWeight: '600'
        }}>
          $4.99/mo
        </span>
      </button>
    );
  }
  
  return (
    <div 
      data-testid="micros-preview"
      style={{
        background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.06) 0%, rgba(17, 153, 142, 0.04) 100%)',
        border: '1px solid rgba(56, 239, 125, 0.2)',
        padding: `${spacing.s3} ${spacing.s4}`,
        marginBottom: spacing.s2,
        borderRadius: radius.md
      }}
    >
      {/* Smart Plaite Badge Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: spacing.s2
      }}>
        <span style={{ 
          fontSize: '9px', 
          color: '#38ef7d', 
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          🎯 Smart Plaite
        </span>
      </div>
      
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0
        }}
      >
        <span style={{ fontSize: typography.bodySmall.fontSize, color: colors.text }}>
          📊 Micronutrients: <strong>{avgPercent}%</strong> of daily targets
        </span>
        {expanded ? <ChevronUp size={16} color={colors.textSecondary} /> : <ChevronDown size={16} color={colors.textSecondary} />}
      </button>
      
      {expanded && (
        <div style={{ marginTop: spacing.s3, display: 'flex', flexDirection: 'column', gap: spacing.s2 }}>
          {/* Priority Limits Section */}
          <div style={{ marginBottom: '4px' }}>
            <span style={{ fontSize: '10px', color: colors.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Daily Limits (stay under)
            </span>
          </div>
          {displayItems.filter(item => item.isLimit).map((item, idx) => {
            const percent = Math.min(100, Math.round((item.value / item.rda) * 100));
            const isOver = item.value > item.rda;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
                <span style={{ width: '20px', fontSize: typography.bodySmall.fontSize }}>{item.icon}</span>
                <span style={{ width: '80px', fontSize: '11px', color: colors.text, fontWeight: '500' }}>
                  {item.label}
                </span>
                <div style={{ flex: 1, height: '6px', background: colors.panel2, borderRadius: radius.sm, overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${percent}%`, 
                    height: '100%', 
                    background: isOver ? colors.error : percent > 75 ? colors.warning : colors.success,
                    borderRadius: radius.sm,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <span style={{ 
                  minWidth: '70px', 
                  fontSize: '10px', 
                  color: isOver ? colors.error : colors.textSecondary, 
                  textAlign: 'right',
                  fontWeight: isOver ? '600' : '400'
                }}>
                  {Math.round(item.value)}/{item.rda}{item.unit}
                </span>
              </div>
            );
          })}
          
          {/* Goals Section */}
          <div style={{ marginTop: spacing.s2, marginBottom: '4px' }}>
            <span style={{ fontSize: '10px', color: colors.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Daily Goals (aim for)
            </span>
          </div>
          {displayItems.filter(item => !item.isLimit).map((item, idx) => {
            const percent = Math.min(100, Math.round((item.value / item.rda) * 100));
            const isMet = item.value >= item.rda;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
                <span style={{ width: '20px', fontSize: typography.bodySmall.fontSize }}>{item.icon}</span>
                <span style={{ width: '80px', fontSize: '11px', color: colors.text, fontWeight: '500' }}>
                  {item.label}
                </span>
                <div style={{ flex: 1, height: '6px', background: colors.panel2, borderRadius: radius.sm, overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${percent}%`, 
                    height: '100%', 
                    background: isMet ? colors.success : colors.accent,
                    borderRadius: radius.sm,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <span style={{ 
                  minWidth: '70px', 
                  fontSize: '10px', 
                  color: isMet ? colors.success : colors.textSecondary, 
                  textAlign: 'right',
                  fontWeight: isMet ? '600' : '400'
                }}>
                  {Math.round(item.value)}/{item.rda}{item.unit}
                </span>
              </div>
            );
          })}
          
          {/* RDA Note */}
          <div style={{ 
            marginTop: spacing.s2, 
            padding: spacing.s2, 
            background: colors.accentGlow, 
            borderRadius: radius.sm,
            fontSize: '10px',
            color: colors.textSecondary
          }}>
            💡 RDA = Recommended Daily Allowance based on a 2000 calorie diet
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// HYDRATION STRIP - v1.3 MVP - Compact hydration status for home screen
// Shows: "💧 Hydration: X L / Y L" with progress bar
// Available to ALL tiers
// ═══════════════════════════════════════════════════════════════════════════════
const HydrationStrip = ({ 
  hydrationMl = 0, 
  hydrationGoalMl = 2500, 
  onTap 
}) => {
  const liters = (hydrationMl / 1000).toFixed(1);
  const goalLiters = (hydrationGoalMl / 1000).toFixed(1);
  const percent = Math.min(100, Math.round((hydrationMl / hydrationGoalMl) * 100));
  const isGoalMet = hydrationMl >= hydrationGoalMl;
  const hasLogged = hydrationMl > 0;
  
  return (
    <button
      data-testid="hydration-strip"
      onClick={onTap}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${spacing.s3} ${spacing.s4}`,
        background: isGoalMet 
          ? 'linear-gradient(135deg, rgba(56, 189, 248, 0.15) 0%, rgba(56, 239, 125, 0.1) 100%)'
          : hasLogged 
            ? 'rgba(56, 189, 248, 0.08)'
            : 'rgba(56, 189, 248, 0.05)',
        border: `1px solid ${isGoalMet ? 'rgba(56, 239, 125, 0.3)' : hasLogged ? 'rgba(56, 189, 248, 0.2)' : 'rgba(56, 189, 248, 0.15)'}`,
        borderRadius: radius.md,
        cursor: 'pointer',
        marginBottom: spacing.s3
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
        <span style={{ fontSize: '16px' }}>💧</span>
        <div>
          {hasLogged ? (
            <span style={{ 
              fontSize: typography.body.fontSize, 
              fontWeight: '600', 
              color: isGoalMet ? colors.success : '#38bdf8'
            }}>
              Hydration: {liters} L / {goalLiters} L
              {isGoalMet && <span style={{ marginLeft: '6px', fontSize: '12px' }}>✓</span>}
            </span>
          ) : (
            <span style={{ 
              fontSize: typography.body.fontSize, 
              fontWeight: '600', 
              color: '#38bdf8'
            }}>
              Log Hydration
              <span style={{ 
                marginLeft: '8px', 
                fontSize: '11px', 
                fontWeight: '400',
                color: colors.textSecondary
              }}>
                Goal: {goalLiters} L
              </span>
            </span>
          )}
        </div>
      </div>
      
      {/* Mini progress bar or tap prompt */}
      {hasLogged ? (
        <div style={{ 
          width: '60px', 
          height: '6px', 
          background: colors.panel2, 
          borderRadius: radius.sm,
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: `${percent}%`, 
            height: '100%', 
            background: isGoalMet 
              ? `linear-gradient(90deg, #38bdf8, ${colors.success})`
              : '#38bdf8',
            borderRadius: radius.sm,
            transition: 'width 0.3s ease'
          }} />
        </div>
      ) : (
        <span style={{ 
          fontSize: '11px', 
          color: '#38bdf8',
          fontWeight: '500'
        }}>
          Tap to log →
        </span>
      )}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DAILY CHECK-IN STRIP - Compact strip to prompt daily wellness check-in
// Available to ALL tiers
// ═══════════════════════════════════════════════════════════════════════════════
const DailyCheckinStrip = ({ onTap }) => {
  return (
    <button
      data-testid="daily-checkin-strip"
      onClick={onTap}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${spacing.s3} ${spacing.s4}`,
        background: 'rgba(56, 239, 125, 0.05)',
        border: '1px solid rgba(56, 239, 125, 0.15)',
        borderRadius: radius.md,
        cursor: 'pointer',
        marginBottom: spacing.s3
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
        <span style={{ fontSize: '16px' }}>☀️</span>
        <div>
          <span style={{ 
            fontSize: typography.body.fontSize, 
            fontWeight: '600', 
            color: '#38ef7d'
          }}>
            Daily Check-in
          </span>
          <span style={{ 
            marginLeft: '8px', 
            fontSize: '11px', 
            fontWeight: '400',
            color: colors.textSecondary
          }}>
            Sleep, energy, mood & more
          </span>
        </div>
      </div>
      <span style={{ 
        fontSize: '11px', 
        color: '#38ef7d',
        fontWeight: '500'
      }}>
        Log →
      </span>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SMART TIMER PILL - Compact green pill banner for meal timing & fasting guidance
// Smart Plaite+ feature - Free users see locked state
// ═══════════════════════════════════════════════════════════════════════════════
const EatingWindowPill = ({ isActive, timeRemaining, windowType, onPress, userTier, onUpgradeSmart, calorieWindow }) => {
  const isLocked = userTier === 'free';
  
  // isActive means the IF timer is running = user is FASTING = eating window CLOSED
  // When timer is running, timeRemaining = time until fast ends (eating window opens)
  const isFasting = isActive;
  
  const formatTime = (seconds) => {
    if (!seconds) return '';
    // ifTimeRemaining is in SECONDS, not minutes
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };
  
  // Format reset time for display (HH:MM -> readable)
  const formatResetTime = (time) => {
    if (!time || time === '00:00') return 'Midnight';
    const [hour] = time.split(':').map(Number);
    if (hour === 0) return '12:00 AM';
    if (hour === 12) return '12:00 PM';
    if (hour > 12) return `${hour - 12}:00 PM`;
    return `${hour}:00 AM`;
  };
  
  // Check if user has a custom calorie window set
  const hasCustomWindow = calorieWindow && calorieWindow.reset_time && calorieWindow.reset_time !== '00:00';
  const scheduleType = calorieWindow?.schedule_type;
  const isIF = scheduleType === 'intermittent_fasting';
  const isCustom = scheduleType === 'custom';
  
  // Locked state for Free tier
  if (isLocked) {
    return (
      <button
        data-testid="smart-timer-pill-locked"
        onClick={onUpgradeSmart}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${spacing.s2} ${spacing.s3}`,
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${colors.strokeLight}`,
          borderRadius: radius.pill,
          cursor: 'pointer',
          marginBottom: spacing.s3,
          opacity: 0.8
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: colors.textSecondary }}>
              Smart Timer
            </span>
            <span style={{ fontSize: '10px', color: colors.textMuted }}>
              Unlock meal timing & custom schedules
            </span>
          </div>
        </div>
        <div style={{ 
          fontSize: '10px', 
          fontWeight: '600', 
          color: '#38ef7d',
          background: 'rgba(56, 239, 125, 0.15)',
          padding: '2px 8px',
          borderRadius: radius.full
        }}>
          $4.99/mo
        </div>
      </button>
    );
  }
  
  return (
    <button
      data-testid="smart-timer-pill"
      onClick={onPress}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${spacing.s2} ${spacing.s3}`,
        background: isFasting 
          ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.12) 0%, rgba(255, 159, 67, 0.08) 100%)'
          : isIF
            ? 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%)'
            : isCustom
              ? 'linear-gradient(135deg, rgba(255, 159, 67, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%)'
              : 'rgba(255,255,255,0.03)',
        border: `1px solid ${
          isFasting ? 'rgba(255, 107, 107, 0.3)' 
          : isIF ? 'rgba(56, 239, 125, 0.2)'
          : isCustom ? 'rgba(255, 159, 67, 0.2)'
          : colors.strokeLight
        }`,
        borderRadius: radius.pill,
        cursor: 'pointer',
        marginBottom: spacing.s3
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '14px' }}>
          {isFasting ? '🔴' : isIF ? '⏰' : isCustom ? '🔧' : '⏱️'}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {isFasting ? (
            <>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#ff6b6b' }}>
                Eating Window Closed
              </span>
              {timeRemaining && (
                <span style={{ fontSize: '10px', color: colors.textMuted }}>
                  Opens in {formatTime(timeRemaining)}
                </span>
              )}
            </>
          ) : hasCustomWindow ? (
            <>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                color: isIF ? 'rgba(56, 239, 125, 0.9)' : isCustom ? 'rgba(255, 159, 67, 0.9)' : colors.textSecondary 
              }}>
                {isIF 
                  ? `IF ${calorieWindow.if_preference?.replace('_', ':') || '16:8'}`
                  : 'Custom Schedule'}
              </span>
              <span style={{ fontSize: '10px', color: colors.textMuted }}>
                {isIF && calorieWindow.eating_window_start
                  ? `Eating: ${formatResetTime(calorieWindow.eating_window_start)} → ${formatResetTime(calorieWindow.reset_time)}`
                  : `Day resets at ${formatResetTime(calorieWindow.reset_time)}`}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: '12px', fontWeight: '600', color: colors.textSecondary }}>
                Smart Timer
              </span>
              <span style={{ fontSize: '10px', color: colors.textMuted }}>
                Set up fasting or custom schedule
              </span>
            </>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {isFasting && (
          <span style={{ 
            fontSize: '9px', 
            background: 'rgba(255, 107, 107, 0.15)',
            color: '#ff6b6b',
            padding: '3px 8px',
            borderRadius: radius.sm,
            fontWeight: '600'
          }}>
            FASTING
          </span>
        )}
        {hasCustomWindow && !isFasting && (
          <span style={{ 
            fontSize: '9px', 
            background: isIF ? 'rgba(56, 239, 125, 0.15)' : 'rgba(255, 159, 67, 0.15)',
            color: isIF ? 'rgba(56, 239, 125, 0.9)' : 'rgba(255, 159, 67, 0.9)',
            padding: '3px 8px',
            borderRadius: radius.sm,
            fontWeight: '600'
          }}>
            {isIF ? 'IF' : 'CUSTOM'}
          </span>
        )}
        <ChevronRight size={16} color={isFasting ? '#ff6b6b' : isIF ? 'rgba(56, 239, 125, 0.9)' : isCustom ? 'rgba(255, 159, 67, 0.9)' : colors.textSecondary} />
      </div>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SMART ACTIONS CARD - Permanent feature for Genius Plaite users
// Three square buttons: SmartMeals, SmartSwaps, Fit My Macros
// ═══════════════════════════════════════════════════════════════════════════════
const SmartActionsCard = ({ 
  hasGeniusTier, 
  onSmartMeals, 
  onSmartSwaps, 
  onFitMyMacros,
  onUpgradeGenius 
}) => {
  // Locked state for non-Genius users
  if (!hasGeniusTier) {
    return (
      <div 
        data-testid="smart-actions-card-locked"
        onClick={onUpgradeGenius}
        style={{
          padding: `${spacing.s3} ${spacing.s4}`,
          marginBottom: spacing.s3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, rgba(118, 75, 162, 0.04) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.15)',
          borderRadius: radius.lg,
          cursor: 'pointer',
          opacity: 0.85
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: spacing.s2 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>🍽️</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              Not sure what to eat?
            </span>
            <span style={{ fontSize: '12px' }}>🔒</span>
          </div>
          <span style={{ 
            fontSize: '10px', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            padding: '3px 8px',
            borderRadius: radius.pill,
            fontWeight: '600'
          }}>
            Genius
          </span>
        </div>
        <p style={{ 
          margin: 0, 
          fontSize: '11px', 
          color: colors.textSecondary,
          lineHeight: '1.4'
        }}>
          Unlock AI meal planning, smart swaps, and quick meal ideas
        </p>
      </div>
    );
  }
  
  // Unlocked state for Genius users - Three square buttons
  return (
    <div 
      data-testid="smart-actions-card"
      style={{
        padding: `${spacing.s3} ${spacing.s4}`,
        marginBottom: spacing.s3,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.06) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: radius.lg
      }}
    >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: spacing.s3
      }}>
        <span style={{ fontSize: '16px' }}>🍽️</span>
        <div>
          <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text, display: 'block' }}>
            Not sure what to eat?
          </span>
          <span style={{ fontSize: '11px', color: colors.textSecondary }}>
            Plan ahead or get quick suggestions
          </span>
        </div>
      </div>
      
      {/* Three Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: spacing.s2,
        justifyContent: 'space-between'
      }}>
        {/* SmartMeals Button */}
        <button
          data-testid="smart-actions-meals-btn"
          onClick={onSmartMeals}
          style={{
            flex: 1,
            aspectRatio: '1',
            maxWidth: '100px',
            padding: spacing.s2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            transition: 'transform 0.15s ease, opacity 0.15s ease'
          }}
        >
          <span style={{ fontSize: '24px' }}>🍽️</span>
          <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center' }}>
            SmartMeals
          </span>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>
            Meal Planner
          </span>
        </button>
        
        {/* SmartSwaps Button */}
        <button
          data-testid="smart-actions-swaps-btn"
          onClick={onSmartSwaps}
          style={{
            flex: 1,
            aspectRatio: '1',
            maxWidth: '100px',
            padding: spacing.s2,
            background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            boxShadow: '0 2px 8px rgba(56, 239, 125, 0.3)',
            transition: 'transform 0.15s ease, opacity 0.15s ease'
          }}
        >
          <span style={{ fontSize: '24px' }}>🔄</span>
          <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center' }}>
            SmartSwaps
          </span>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>
            Suggestions
          </span>
        </button>
        
        {/* Quick Meal Ideas Button */}
        <button
          data-testid="smart-actions-fit-macros-btn"
          onClick={onFitMyMacros}
          style={{
            flex: 1,
            aspectRatio: '1',
            maxWidth: '100px',
            padding: spacing.s2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)',
            transition: 'transform 0.15s ease, opacity 0.15s ease'
          }}
        >
          <span style={{ fontSize: '24px' }}>🎯</span>
          <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center' }}>
            Fit Macros
          </span>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>
            Hit Goals
          </span>
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SMART HUB PREVIEW CARD - Entry point to Smart Hub with descriptive copy
// ═══════════════════════════════════════════════════════════════════════════════
const SmartHubPreviewCard = ({ onNavigate, hasGeniusTier, onUpgradeGenius }) => (
  <div 
    data-testid="smart-hub-preview-card"
    onClick={hasGeniusTier ? onNavigate : onUpgradeGenius}
    style={{
      padding: `${spacing.s3} ${spacing.s4}`,
      marginBottom: spacing.s3,
      background: hasGeniusTier 
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.05) 100%)'
        : 'linear-gradient(135deg, rgba(100, 100, 100, 0.08) 0%, rgba(80, 80, 80, 0.05) 100%)',
      border: hasGeniusTier 
        ? '1px solid rgba(102, 126, 234, 0.2)'
        : '1px solid rgba(100, 100, 100, 0.3)',
      borderRadius: radius.md,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      opacity: hasGeniusTier ? 1 : 0.85
    }}
  >
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '16px' }}>⭐</span>
        <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>Smart Hub</span>
        {!hasGeniusTier && <span style={{ fontSize: '12px' }}>🔒</span>}
      </div>
      <span style={{ fontSize: '11px', color: colors.textSecondary, marginLeft: '24px', display: 'block', marginTop: '2px' }}>
        Favorites • Recipes • Today
      </span>
    </div>
    <ChevronRight size={18} color={colors.textSecondary} />
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// INTELLIGENCE UPSELL SECTION - Appears AFTER core daily content
// Updated with brighter CTAs and refined copy
// ═══════════════════════════════════════════════════════════════════════════════
const IntelligenceUpsellSection = ({ 
  userTier, 
  onSmartGoals, 
  onDailyCheckin, 
  onBarcode,
  onMicronutrients,
  onSmartMeals,
  onSmartSwaps,
  onSmartHub,
  onMIQView,
  onUpgradeSmart,
  onUpgradeGenius,
  isCheckingOut
}) => {
  // Determine if user has each tier
  const hasSmartTier = userTier === 'enhanced' || userTier === 'smart' || userTier === 'miq' || userTier === 'genius';
  const hasGeniusTier = userTier === 'miq' || userTier === 'genius';
  
  return (
  <div data-testid="upsell-section" style={{ marginBottom: spacing.s2 }}>
    {/* Smart Plaite - $4.99/mo - Compact */}
    <div 
      style={{
        padding: '12px 14px',
        marginBottom: spacing.s2,
        background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(17, 153, 142, 0.06) 100%)',
        border: '1px solid rgba(56, 239, 125, 0.25)',
        borderRadius: radius.lg
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div>
          <span style={{ fontSize: '13px', fontWeight: '700', color: colors.text }}>
            🎯 Smart Plaite
          </span>
          <span style={{ fontSize: '10px', color: colors.textSecondary, marginLeft: '6px' }}>
            Plan Your Nutrition
          </span>
        </div>
        <span style={{ 
          fontSize: '10px', 
          background: hasSmartTier ? 'rgba(56, 239, 125, 0.3)' : 'rgba(56, 239, 125, 0.2)', 
          color: '#38ef7d',
          padding: '3px 8px',
          borderRadius: radius.pill,
          fontWeight: '700'
        }}>
          {hasSmartTier ? '✓ Active' : '$4.99/mo'}
        </span>
      </div>
      
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        <button onClick={onSmartGoals} style={{ flex: 1, padding: '6px 4px', background: 'rgba(56, 239, 125, 0.1)', border: '1px solid rgba(56, 239, 125, 0.25)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>🎯</span>
          <span style={{ fontSize: '8px', color: colors.text }}>Goals</span>
        </button>
        <button onClick={onDailyCheckin} style={{ flex: 1, padding: '6px 4px', background: 'rgba(56, 239, 125, 0.1)', border: '1px solid rgba(56, 239, 125, 0.25)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>☀️</span>
          <span style={{ fontSize: '8px', color: colors.text }}>Check-in</span>
        </button>
        <button data-testid="upsell-barcode-scan" onClick={onBarcode} style={{ flex: 1, padding: '6px 4px', background: 'rgba(56, 239, 125, 0.1)', border: '1px solid rgba(56, 239, 125, 0.25)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center' }}>
          <span style={{ fontSize: '11px', display: 'block', fontFamily: 'monospace', fontWeight: '900' }}>||||</span>
          <span style={{ fontSize: '8px', color: colors.text }}>Barcode</span>
        </button>
        <button onClick={onMicronutrients} style={{ flex: 1, padding: '6px 4px', background: 'rgba(56, 239, 125, 0.1)', border: '1px solid rgba(56, 239, 125, 0.25)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>📊</span>
          <span style={{ fontSize: '8px', color: colors.text }}>Micros</span>
        </button>
      </div>
      
      <button
        data-testid="upgrade-smart-btn"
        onClick={onUpgradeSmart}
        disabled={hasSmartTier || isCheckingOut === 'smart'}
        style={{
          width: '100%',
          padding: '8px 12px',
          background: hasSmartTier 
            ? 'rgba(56, 239, 125, 0.1)' 
            : 'linear-gradient(135deg, #4afa8b 0%, #38ef7d 50%, #11998e 100%)',
          border: hasSmartTier ? '1px solid rgba(56, 239, 125, 0.3)' : 'none',
          borderRadius: radius.md,
          color: hasSmartTier ? '#38ef7d' : '#000',
          fontSize: '12px',
          fontWeight: '700',
          cursor: hasSmartTier ? 'default' : 'pointer',
          opacity: isCheckingOut === 'smart' ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          boxShadow: hasSmartTier ? 'none' : '0 4px 15px rgba(56, 239, 125, 0.35)'
        }}
      >
        {isCheckingOut === 'smart' ? (
          <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Redirecting...</>
        ) : hasSmartTier ? (
          <>✓ Current Plan</>
        ) : (
          <><Zap size={14} /> Build My Plan</>
        )}
      </button>
    </div>
    
    {/* Genius Plaite - $9.99/mo - Compact */}
    <div 
      style={{
        padding: '12px 14px',
        background: `linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(102, 126, 234, 0.06) 100%)`,
        border: `1px solid rgba(147, 51, 234, 0.25)`,
        borderRadius: radius.lg
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div>
          <span style={{ fontSize: '13px', fontWeight: '700', color: colors.text }}>
            🧠 Genius Plaite
          </span>
          <span style={{ fontSize: '10px', color: '#667eea', marginLeft: '6px' }}>
            AI Nutrition Coach
          </span>
        </div>
        <span style={{ 
          fontSize: '10px', 
          background: hasGeniusTier ? gradients.primary : gradients.primary, 
          color: colors.text,
          padding: '3px 8px',
          borderRadius: radius.pill,
          fontWeight: '700'
        }}>
          {hasGeniusTier ? '✓ Active' : '$9.99/mo'}
        </span>
      </div>
      
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        <button onClick={hasGeniusTier ? onMIQView : onUpgradeGenius} style={{ flex: 1, padding: '6px 4px', background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.2)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>🧠</span>
          <span style={{ fontSize: '7px', color: colors.text }}>MIQ</span>
          {!hasGeniusTier && <span style={{ position: 'absolute', top: '1px', right: '1px', fontSize: '7px' }}>🔒</span>}
        </button>
        <button data-testid="smart-swaps-btn" onClick={hasGeniusTier ? onSmartSwaps : onUpgradeGenius} style={{ flex: 1, padding: '6px 4px', background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.2)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>🔄</span>
          <span style={{ fontSize: '7px', color: colors.text }}>Picks</span>
          {!hasGeniusTier && <span style={{ position: 'absolute', top: '1px', right: '1px', fontSize: '7px' }}>🔒</span>}
        </button>
        <button data-testid="smart-hub-btn" onClick={hasGeniusTier ? onSmartHub : onUpgradeGenius} style={{ flex: 1, padding: '6px 4px', background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.2)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>📊</span>
          <span style={{ fontSize: '7px', color: colors.text }}>Coach</span>
          {!hasGeniusTier && <span style={{ position: 'absolute', top: '1px', right: '1px', fontSize: '7px' }}>🔒</span>}
        </button>
        <button onClick={hasGeniusTier ? onSmartMeals : onUpgradeGenius} style={{ flex: 1, padding: '6px 4px', background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.2)', borderRadius: radius.sm, cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
          <span style={{ fontSize: '12px', display: 'block' }}>🍽️</span>
          <span style={{ fontSize: '7px', color: colors.text }}>Meals</span>
          {!hasGeniusTier && <span style={{ position: 'absolute', top: '1px', right: '1px', fontSize: '7px' }}>🔒</span>}
        </button>
      </div>
      
      <button
        data-testid="upgrade-genius-btn"
        onClick={onUpgradeGenius}
        disabled={hasGeniusTier || isCheckingOut === 'genius'}
        style={{
          width: '100%',
          padding: '8px 12px',
          background: hasGeniusTier 
            ? 'rgba(147, 51, 234, 0.1)' 
            : 'linear-gradient(135deg, #8b7cf5 0%, #667eea 50%, #9333ea 100%)',
          border: hasGeniusTier ? '1px solid rgba(147, 51, 234, 0.3)' : 'none',
          borderRadius: radius.md,
          color: hasGeniusTier ? '#9333ea' : '#fff',
          fontSize: '12px',
          fontWeight: '700',
          cursor: hasGeniusTier ? 'default' : 'pointer',
          opacity: isCheckingOut === 'genius' ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          boxShadow: hasGeniusTier ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.35)'
        }}
      >
        {isCheckingOut === 'genius' ? (
          <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Redirecting...</>
        ) : hasGeniusTier ? (
          <>✓ Current Plan</>
        ) : (
          <><Star size={14} /> Get Genius Plaite</>
        )}
      </button>
    </div>
  </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FOOTER - Legal disclaimer + AI-Powered text + Timezone indicator
// ═══════════════════════════════════════════════════════════════════════════════
const HomeFooter = ({ userTimezone, onOpenSettings }) => (
  <footer 
    data-testid="home-footer"
    style={{
      padding: `${spacing.s4} ${spacing.s3}`,
      textAlign: 'center',
      borderTop: `1px solid ${colors.stroke}`
    }}
  >
    {/* Timezone indicator - Prominent for user visibility */}
    <div 
      data-testid="timezone-indicator"
      onClick={onOpenSettings}
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '16px',
        padding: '10px 16px',
        cursor: 'pointer',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.4)',
        borderRadius: '20px',
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
      }}
    >
      <span style={{ fontSize: '14px' }}>🌍</span>
      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '600' }}>
        UTC{(userTimezone ?? 0) >= 0 ? '+' : ''}{userTimezone ?? 0}
      </span>
      <span style={{ fontSize: '11px', color: colors.textSecondary, borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '8px' }}>
        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span style={{ fontSize: '10px', color: colors.accent }}>
        Tap to change ⚙️
      </span>
    </div>
    
    <p style={{ 
      margin: `0 0 ${spacing.s2} 0`, 
      fontSize: '9px', 
      color: colors.mutedText,
      lineHeight: '1.4'
    }}>
      SmartPlAIte provides nutritional guidance based on your data. 
      Consult a healthcare professional for medical advice.
    </p>
    <p style={{ 
      margin: 0, 
      fontSize: '10px', 
      color: colors.textSecondary,
      fontWeight: '500'
    }}>
      🧠 AI-Powered Nutrition Intelligence
    </p>
  </footer>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOME VIEW COMPONENT
// Frictionless daily workflow: Log → View Progress → View Meals → Optional deeper insights → Optional upgrade
// ═══════════════════════════════════════════════════════════════════════════════
const HomeView = ({
  // Data
  todayTotals,
  dailyGoal,
  targetProtein,
  targetCarbs,
  targetFat,
  todayMeals,
  favorites,
  exercises,
  totalCaloriesBurned,
  dailySteps,
  micros,
  rdaValues,
  hasAiGoals,
  userTier,
  miqStatus,
  miqFactorsActive,
  miqTotalFactors,
  flexibleMacroMode = false, // New: Flexible Macro Mode flag
  // MIQ Calibration Banner props
  miqCalibrationDay,
  miqIsCalibrating,
  miqCalibrationComplete,
  miqBaselineViewedAt,
  isGeniusTier,
  isSignupTrial, // New: Whether user is on free 30-day trial
  onViewBaseline,
  onUpgradeFromMIQ,
  // Actions
  onTakePhoto,
  onLogMeal,
  onManualEntry,
  onSetGoals,
  onNavigateToMIQ,
  onFavoriteMeal,
  onRemoveMeal,
  onEditPortion, // Edit meal portion eaten
  onLogActivity,
  onLogSteps,
  onDeleteActivity, // Delete activity
  onUpgradeMicros,
  onMicronutrients,
  onSmartGoals,
  onDailyCheckin,
  onBarcode,
  onSmartMeals,
  onSmartSwaps,
  onFitMyMacros, // Fit My Macros action
  onSmartHub,  // Navigate to Smart Hub
  userTimezone, // User's selected timezone offset
  onOpenSettings, // Navigate to settings
  // Upgrade actions
  onUpgradeSmart,
  onUpgradeGenius,
  isCheckingOut,
  // Health Intelligence props
  onHealthIntelFixThis,
  onHealthIntelAskWhy,
  askWhyUsedToday = 0,
  // Trial & Promo
  userId,
  subscriptionDetails,
  onTrialActivated,
  // Extra components to render
  coachStrip,
  eatingOutBanner,
  featureDiscoveryCard,
  shiftWorkIndicator,
  // Smart Timer props
  ifFastingActive,
  ifTimeRemaining,
  ifWindowType,
  onIFTimer,
  // v1.3 - Hydration tracking props
  hydrationMl = 0,
  hydrationGoalMl = 2500,
  onHydrationTap,
  // Trial countdown banner props
  miqScore,
  totalMealsLogged,
  // Date navigation for past-day editing
  selectedDate,
  onDateChange,
  // Navigation Wheel (section controller)
  navigationWheel,
  openSections = new Set()
}) => {
  const isMicrosLocked = userTier === 'free';
  
  return (
    <div data-testid="home-view-refactored" className="home-view" style={{ paddingBottom: '80px' }}>
      
      {/* ═══ NEW USER ELEMENTS (always visible for onboarding) ═══ */}
      {eatingOutBanner}
      {featureDiscoveryCard}
      {coachStrip}
      
      {/* ═══ ALWAYS VISIBLE: Macro Dashboard ═══ */}
      <TodaysProgressCard
        calories={todayTotals?.calories || 0}
        dailyGoal={dailyGoal || 2000}
        protein={todayTotals?.protein || 0}
        carbs={todayTotals?.carbs || 0}
        fat={todayTotals?.fat || 0}
        fiber={todayTotals?.fiber || 0}
        targetProtein={targetProtein || 150}
        targetCarbs={targetCarbs || 250}
        targetFat={targetFat || 65}
        hasAiGoals={hasAiGoals}
        totalCaloriesBurned={totalCaloriesBurned}
        flexibleMacroMode={flexibleMacroMode}
        calorieWindow={todayTotals?.calorie_window}
      />
      
      {/* ═══ ALWAYS VISIBLE: Feature Compass (Hero Wheel) ═══ */}
      {navigationWheel}
      
      {/* ═══ WHEEL-CONTROLLED SECTIONS ═══ */}
      
      {openSections.has('todays-meals') && (
        <TodaysMealsSection
          meals={todayMeals || []}
          favorites={favorites}
          onFavorite={onFavoriteMeal}
          onRemove={onRemoveMeal}
          onEditPortion={onEditPortion}
          onAddMeal={selectedDate ? onManualEntry : onLogMeal}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
        />
      )}
      
      {openSections.has('activity') && (
        <ActivitySection
          exercises={exercises}
          totalCaloriesBurned={totalCaloriesBurned}
          dailySteps={dailySteps}
          onLogActivity={onLogActivity}
          onLogSteps={onLogSteps}
          onDeleteActivity={onDeleteActivity}
        />
      )}
      
      {openSections.has('checkin') && (
        <DailyCheckinStrip onTap={onDailyCheckin} />
      )}
      
      {openSections.has('micros') && (
        <MicronutrientsPreview
          micros={micros}
          rdaValues={rdaValues}
          isLocked={isMicrosLocked}
          onUpgrade={onUpgradeMicros}
        />
      )}
      
      {openSections.has('hydration') && (
        <HydrationStrip
          hydrationMl={hydrationMl}
          hydrationGoalMl={hydrationGoalMl}
          onTap={onHydrationTap}
        />
      )}
      
      {openSections.has('timer') && (
        <EatingWindowPill
          isActive={ifFastingActive}
          timeRemaining={ifTimeRemaining}
          windowType={ifWindowType}
          onPress={onIFTimer}
          userTier={userTier}
          onUpgradeSmart={onUpgradeSmart}
          calorieWindow={todayTotals?.calorie_window}
        />
      )}
      
      {openSections.has('smart-actions') && (
        <SmartActionsCard
          hasGeniusTier={isGeniusTier}
          onSmartMeals={onSmartMeals}
          onSmartSwaps={onSmartSwaps}
          onFitMyMacros={onFitMyMacros}
          onUpgradeGenius={onUpgradeGenius}
        />
      )}
      
      {openSections.has('health-intel') && (
        <HealthIntelligenceCard
          calories={todayTotals?.calories || 0}
          calorieGoal={dailyGoal || 2000}
          protein={todayTotals?.protein || 0}
          proteinGoal={targetProtein || 150}
          fat={todayTotals?.fat || 0}
          fatGoal={targetFat || 65}
          carbs={todayTotals?.carbs || 0}
          carbsGoal={targetCarbs || 250}
          userTier={userTier}
          isTrial={isSignupTrial}
          onFixThis={onHealthIntelFixThis}
          onAskWhy={onHealthIntelAskWhy}
          onUpgradeGenius={onUpgradeGenius}
          askWhyUsedToday={askWhyUsedToday}
        />
      )}
      
      {openSections.has('smart-hub') && (
        <SmartHubPreviewCard 
          onNavigate={onSmartHub} 
          hasGeniusTier={isGeniusTier}
          onUpgradeGenius={onUpgradeGenius}
        />
      )}
      
      {openSections.has('miq-status') && (
        <MIQStatusStrip 
          status={miqStatus || 'Initializing'}
          factorsActive={miqFactorsActive}
          totalFactors={miqTotalFactors || CONFIDENCE_FACTOR_CONFIG.TOTAL_FACTORS}
          onNavigate={onNavigateToMIQ}
        />
      )}

      {/* ═══ Bloodwork Home Card — always visible entry point ═══ */}
      <BloodworkHomeCard
        userId={userId}
        isPremium={isGeniusTier}
        userTier={userTier}
        onNavigateToMIQ={onNavigateToMIQ}
        onUpgradeGenius={onUpgradeGenius}
      />
      
      {/* ═══ ALWAYS VISIBLE: Tier Upsell Boxes ═══ */}
      <IntelligenceUpsellSection
        userTier={userTier}
        onSmartGoals={onSmartGoals}
        onDailyCheckin={onDailyCheckin}
        onBarcode={onBarcode}
        onMicronutrients={onMicronutrients}
        onSmartMeals={onSmartMeals}
        onSmartSwaps={onSmartSwaps}
        onSmartHub={onSmartHub}
        onMIQView={onNavigateToMIQ}
        onUpgradeSmart={onUpgradeSmart}
        onUpgradeGenius={onUpgradeGenius}
        isCheckingOut={isCheckingOut}
      />
      
      {/* Footer */}
      <HomeFooter userTimezone={userTimezone} onOpenSettings={onOpenSettings} />
    </div>
  );
};

export default HomeView;
export { 
  QuickActionBar, 
  TodaysProgressCard, 
  GoalPrompt, 
  MIQStatusStrip, 
  TodaysMealsSection, 
  DateNavigator,
  ActivitySection,
  MicronutrientsPreview,
  IntelligenceUpsellSection,
  EatingWindowPill,
  SmartActionsCard,
  SmartHubPreviewCard,
  HomeFooter
};
